import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt, faHome, faBriefcase, faEllipsisH,
  faPlus, faEdit, faTrash, faCheck, faSpinner,
  faTimes, faStar
} from '@fortawesome/free-solid-svg-icons';
import { userAPI } from '../services/api';
import './AddressManagement.css';

/**
 * AddressManagement Component
 *
 * A reusable component for managing user addresses across all dashboards.
 * Matches the mobile app's address management pattern.
 *
 * Data Structure (matching backend):
 * {
 *   id: string,
 *   type: 'home' | 'work' | 'other',
 *   street: string,
 *   city: string,
 *   state: string,
 *   country: string,
 *   postal_code: string,
 *   landmark: string,
 *   isDefault: boolean
 * }
 */

const AddressManagement = ({ onToast, isDarkMode = false, onSelectAddress = null, onAddressChange = null }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    type: 'home',
    street: '',
    city: '',
    state: '',
    country: 'Nigeria',
    postal_code: '',
    landmark: '',
    isDefault: false
  });

  // Fetch addresses on mount
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAddresses();
      if (response && response.data) {
        const addressList = response.data.addresses || response.data || [];
        const list = Array.isArray(addressList) ? addressList : [];
        setAddresses(list);
        if (onAddressChange) onAddressChange(list);
      }
    } catch (error) {
      showToast('Failed to load addresses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'info') => {
    if (onToast) {
      onToast({ message, type });
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'home',
      street: '',
      city: '',
      state: '',
      country: 'Nigeria',
      postal_code: '',
      landmark: '',
      isDefault: false
    });
    setEditingAddress(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, type }));
  };

  const validateForm = () => {
    if (!formData.street.trim()) {
      showToast('Street address is required', 'error');
      return false;
    }
    if (!formData.city.trim()) {
      showToast('City is required', 'error');
      return false;
    }
    if (!formData.state.trim()) {
      showToast('State is required', 'error');
      return false;
    }
    if (!formData.country.trim()) {
      showToast('Country is required', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const addressData = {
        type: formData.type,
        street: formData.street.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country.trim(),
        postal_code: formData.postal_code.trim(),
        landmark: formData.landmark.trim(),
        isDefault: formData.isDefault || addresses.length === 0 // First address is always default
      };

      if (editingAddress) {
        // Update existing address
        await userAPI.updateAddress(editingAddress.id || editingAddress._id, addressData);
        showToast('Address updated successfully', 'success');
      } else {
        // Create new address
        await userAPI.createAddress(addressData);
        showToast('Address added successfully', 'success');
      }

      await fetchAddresses();
      resetForm();
    } catch (error) {
      if (!error.isAuthError) showToast(error.message || 'Failed to save address', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      type: address.type || 'home',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || 'Nigeria',
      postal_code: address.postal_code || address.postalCode || '',
      landmark: address.landmark || '',
      isDefault: address.isDefault || address.is_default || false
    });
    setEditingAddress(address);
    setShowAddForm(true);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      setDeletingId(addressId);
      await userAPI.deleteAddress(addressId);
      showToast('Address deleted successfully', 'success');
      await fetchAddresses();
    } catch (error) {
      if (!error.isAuthError) showToast(error.message || 'Failed to delete address', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      setSettingDefaultId(addressId);
      await userAPI.setDefaultAddress(addressId);
      showToast('Default address updated', 'success');
      await fetchAddresses();
    } catch (error) {
      if (!error.isAuthError) showToast(error.message || 'Failed to set default address', 'error');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'home': return faHome;
      case 'work': return faBriefcase;
      default: return faEllipsisH;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'home': return 'Home';
      case 'work': return 'Work';
      default: return 'Other';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={`address-management ${isDarkMode ? 'dark' : ''}`}>
        <div className="address-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`address-management ${isDarkMode ? 'dark' : ''}`}>
      <div className="address-header">
        <h2>
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          My Addresses
        </h2>
        {!showAddForm && (
          <button className="btn-add-address" onClick={() => setShowAddForm(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Add Address
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="address-form-container">
          <div className="address-form-header">
            <h3>{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
            <button className="btn-close" onClick={resetForm}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="address-form">
            {/* Address Type Selection */}
            <div className="form-group">
              <label>Address Type</label>
              <div className="type-buttons">
                {['home', 'work', 'other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`type-btn ${formData.type === type ? 'selected' : ''}`}
                    onClick={() => handleTypeSelect(type)}
                  >
                    <FontAwesomeIcon icon={getTypeIcon(type)} />
                    {getTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>

            {/* Street Address */}
            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                placeholder="Enter street address"
                required
              />
            </div>

            {/* City and State Row */}
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="State"
                  required
                />
              </div>
            </div>

            {/* Country and Postal Code Row */}
            <div className="form-row">
              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Country"
                  required
                />
              </div>
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  placeholder="Postal code"
                />
              </div>
            </div>

            {/* Landmark */}
            <div className="form-group">
              <label>Landmark (Optional)</label>
              <input
                type="text"
                name="landmark"
                value={formData.landmark}
                onChange={handleInputChange}
                placeholder="Nearby landmark for easier delivery"
              />
            </div>

            {/* Set as Default Checkbox */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Set as default address
              </label>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={submitting}>
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    {editingAddress ? 'Update Address' : 'Save Address'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Address List */}
      <div className="address-list">
        {addresses.length === 0 ? (
          <div className="address-empty">
            <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
            <h3>No addresses yet</h3>
            <p>Add your first delivery address to get started.</p>
            <button className="btn-add-first" onClick={() => setShowAddForm(true)}>
              <FontAwesomeIcon icon={faPlus} />
              Add Address
            </button>
          </div>
        ) : (
          addresses.map((address) => {
            const addressId = address.id || address._id;
            const isDefault = address.isDefault || address.is_default;

            return (
              <div
                key={addressId}
                className={`address-card ${isDefault ? 'default' : ''} ${onSelectAddress ? 'selectable' : ''}`}
                onClick={() => onSelectAddress && onSelectAddress(address)}
              >
                <div className="address-card-header">
                  <div className="address-type">
                    <FontAwesomeIcon icon={getTypeIcon(address.type)} />
                    <span>{getTypeLabel(address.type)}</span>
                  </div>
                  {isDefault && (
                    <span className="default-badge">
                      <FontAwesomeIcon icon={faStar} />
                      Default
                    </span>
                  )}
                </div>

                <div className="address-card-body">
                  <p className="address-street">{address.street}</p>
                  <p className="address-location">
                    {address.city}, {address.state} {address.postal_code || address.postalCode}
                  </p>
                  <p className="address-country">{address.country}</p>
                  {address.landmark && (
                    <p className="address-landmark">
                      <em>Landmark: {address.landmark}</em>
                    </p>
                  )}
                </div>

                <div className="address-card-actions">
                  {!isDefault && (
                    <button
                      className="btn-action btn-default"
                      onClick={(e) => { e.stopPropagation(); handleSetDefault(addressId); }}
                      disabled={settingDefaultId === addressId}
                    >
                      {settingDefaultId === addressId ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faStar} />
                          Set Default
                        </>
                      )}
                    </button>
                  )}
                  <button
                    className="btn-action btn-edit"
                    onClick={(e) => { e.stopPropagation(); handleEdit(address); }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    Edit
                  </button>
                  <button
                    className="btn-action btn-delete"
                    onClick={(e) => { e.stopPropagation(); handleDelete(addressId); }}
                    disabled={deletingId === addressId}
                  >
                    {deletingId === addressId ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faTrash} />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AddressManagement;
